import graphql from 'babel-plugin-relay/macro';
import {FormikHelpers} from 'formik';
import React from 'react';
import {useLazyLoadQuery, useMutation} from 'react-relay/hooks';
import {useHistory, useParams} from 'react-router';
import {showEventNotification} from 'src/utils/notifications';
import {withSuspense} from 'src/utils/withSuspense';

import Form from '../components/Form';
import {FormValues} from '../types';
import {updatePageUpdateValveModelMutation} from './__generated__/updatePageUpdateValveModelMutation.graphql';
import {updatePageValveModelQuery} from './__generated__/updatePageValveModelQuery.graphql';

function UpdatePage() {
  const {id} = useParams<{id: string}>();
  const history = useHistory();

  const [commit, isInFlight] = useMutation<updatePageUpdateValveModelMutation>(
    updateValveModelMutation,
  );

  const data = useLazyLoadQuery<updatePageValveModelQuery>(query, {
    valveModelId: id,
  });

  if (!data.valveModel) {
    return (
      <div className="flex flex-1 items-center justify-center">
        <h4 className="text-lg text-gray-700">The item you wish to update could not be found.</h4>
      </div>
    );
  }

  const initialValues = handleInitialValues();

  return (
    <Form
      title="Update Valve Model"
      button="Update Valve Model"
      initialValues={initialValues}
      onSubmit={handleSubmit}
      isSubmitting={isInFlight}
    />
  );

  function handleInitialValues() {
    if (!data || !data.valveModel) {
      return;
    }

    const {valveModel} = data;

    return {
      name: valveModel.name,
      manufacturerId: valveModel.manufacturer.id,
      familyId: valveModel.family.id,
    };
  }

  function handleSubmit(values: FormValues, formikHelpers: FormikHelpers<FormValues>) {
    const {name, familyId} = values;

    commit({
      variables: {
        input: {
          valveModelId: id,
          updateValveModelData: {name, familyId},
        },
      },
      onCompleted: payload => {
        if (payload.updateValveModel?.errors[0] && payload.updateValveModel?.errors[0].fields) {
          payload.updateValveModel?.errors[0].fields.forEach(fieldError => {
            formikHelpers.setFieldError(fieldError.fieldName, fieldError.message);
          });
        }

        const entry = payload.updateValveModel?.valveModel;

        if (entry) {
          showEventNotification('updated', 'Model', entry.name);

          if (history.location.search.includes('newTab')) {
            window.close();
          }

          return history.push('/admin/valve_models');
        }
      },
    });
  }
}

const query = graphql`
  query updatePageValveModelQuery($valveModelId: ID!) {
    valveModel(valveModelId: $valveModelId) {
      id
      createdAt
      name
      family {
        id
        name
      }
      manufacturer {
        id
        name
      }
    }
  }
`;

const updateValveModelMutation = graphql`
  mutation updatePageUpdateValveModelMutation($input: UpdateValveModelInput!) {
    updateValveModel(input: $input) {
      valveModel {
        id
        createdAt
        name
        family {
          id
          name
        }
        manufacturer {
          id
          name
        }
      }
      errors {
        code
        fields {
          fieldName
          message
        }
      }
    }
  }
`;

export default withSuspense(UpdatePage);
